import * as $float from "../gleam_stdlib/gleam/float.mjs";
import * as $p5js_gleam from "../p5js_gleam/p5js_gleam.mjs";
import * as $p5 from "../p5js_gleam/p5js_gleam/bindings.mjs";
import { Ok, CustomType as $CustomType, makeError } from "./gleam.mjs";
import * as $utils from "./utils.mjs";
import * as $vector from "./vector.mjs";

const FILEPATH = "src/player.gleam";

/**
 * Represents the player character.
 */
export class Player extends $CustomType {
  constructor(position, velocity, acceleration, last_fire_time, last_hit_time, current_health, max_health) {
    super();
    this.position = position;
    this.velocity = velocity;
    this.acceleration = acceleration;
    this.last_fire_time = last_fire_time;
    this.last_hit_time = last_hit_time;
    this.current_health = current_health;
    this.max_health = max_health;
  }
}
export const Player$Player = (position, velocity, acceleration, last_fire_time, last_hit_time, current_health, max_health) =>
  new Player(position,
  velocity,
  acceleration,
  last_fire_time,
  last_hit_time,
  current_health,
  max_health);
export const Player$isPlayer = (value) => value instanceof Player;
export const Player$Player$position = (value) => value.position;
export const Player$Player$0 = (value) => value.position;
export const Player$Player$velocity = (value) => value.velocity;
export const Player$Player$1 = (value) => value.velocity;
export const Player$Player$acceleration = (value) => value.acceleration;
export const Player$Player$2 = (value) => value.acceleration;
export const Player$Player$last_fire_time = (value) => value.last_fire_time;
export const Player$Player$3 = (value) => value.last_fire_time;
export const Player$Player$last_hit_time = (value) => value.last_hit_time;
export const Player$Player$4 = (value) => value.last_hit_time;
export const Player$Player$current_health = (value) => value.current_health;
export const Player$Player$5 = (value) => value.current_health;
export const Player$Player$max_health = (value) => value.max_health;
export const Player$Player$6 = (value) => value.max_health;

/**
 * The size of the player character in pixels.
 */
export const player_size = 10.0;

const max_speed = 4.0;

const jump_power = 0.2;

const time_between_fire = 120;

const invulnerability_time = 500;

const base_max_health = 100;

const player_gravity_strength = 0.02;

const dead_fill_color = "#000000";

const invulnerable_fill_color = "#4c4cff";

const player_fill_color = "#0000ff";

const player_stroke_color = "#000000";

function acceleration() {
  return 2.0 / 3.0;
}

/**
 * Advances the player forward assuming they can.
 */
export function move(player) {
  return new Player(
    $vector.add(player.position, player.velocity),
    player.velocity,
    player.acceleration,
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Accelerates the player in the x direction.
 */
export function accelerate_x(player, forward) {
  let _block;
  if (forward) {
    _block = acceleration();
  } else {
    _block = -1.0 * acceleration();
  }
  let acc = _block;
  return new Player(
    player.position,
    player.velocity,
    new $vector.Vector(acc, player.acceleration.y, player.acceleration.z),
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Accelerates the player in the y direction.
 */
export function accelerate_y(player, forward) {
  let _block;
  if (forward) {
    _block = acceleration();
  } else {
    _block = -1.0 * acceleration();
  }
  let acc = _block;
  return new Player(
    player.position,
    player.velocity,
    new $vector.Vector(player.acceleration.x, acc, player.acceleration.z),
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Stops the player in the x direction.
 */
export function stop_x(player) {
  return new Player(
    player.position,
    new $vector.Vector(0.0, player.velocity.y, player.velocity.z),
    new $vector.Vector(0.0, player.acceleration.y, player.acceleration.z),
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Stops the player in the y direction.
 */
export function stop_y(player) {
  return new Player(
    player.position,
    new $vector.Vector(player.velocity.x, 0.0, player.velocity.z),
    new $vector.Vector(player.acceleration.x, 0.0, player.acceleration.z),
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Applies damage to the player.
 */
export function apply_damage(player, damage) {
  return new Player(
    player.position,
    player.velocity,
    player.acceleration,
    player.last_fire_time,
    $utils.now_in_milliseconds(),
    player.current_health - damage,
    player.max_health,
  );
}

/**
 * Is the player currently dead.
 */
export function is_player_dead(player) {
  return player.current_health <= 0;
}

/**
 * Accelerates the player's velocity.
 */
export function update_velocity(player) {
  let vel = $vector.add(player.velocity, player.acceleration);
  let _block;
  let _pipe = $vector.vector_2d(vel);
  _block = $vector.limit(_pipe, max_speed);
  let limited = _block;
  return new Player(
    player.position,
    new $vector.Vector(limited.x, limited.y, vel.z),
    player.acceleration,
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Let's the player jump if they are on the ground.
 */
export function jump(player) {
  let $ = player.position.z;
  let $1 = player.velocity.z;
  let p = $;
  let v = $1;
  if ((p === 0.0) && (v <= 0.0)) {
    return new Player(
      player.position,
      new $vector.Vector(player.velocity.x, player.velocity.y, jump_power),
      player.acceleration,
      player.last_fire_time,
      player.last_hit_time,
      player.current_health,
      player.max_health,
    );
  } else {
    return player;
  }
}

/**
 * Can the player fire a bullet at the moment.
 */
export function can_player_fire(player) {
  return (player.last_fire_time + time_between_fire) <= $utils.now_in_milliseconds();
}

/**
 * Is the player currently invulnerable.
 */
export function is_player_invulnerable(player) {
  return (player.last_hit_time + invulnerability_time) >= $utils.now_in_milliseconds();
}

/**
 * Makes the starting player at the given position.
 */
export function new_player(initial_position) {
  return new Player(
    initial_position,
    new $vector.Vector(0.0, 0.0, 0.0),
    new $vector.Vector(0.0, 0.0, 0.0),
    0,
    0,
    base_max_health,
    base_max_health,
  );
}

/**
 * Applies gravity to the velocity and resets z position to floor when appropriate.
 */
export function apply_gravity(player) {
  let _block;
  let $ = player.position.z;
  let z = $;
  if (z < 0.0) {
    _block = new $vector.Vector(player.position.x, player.position.y, 0.0);
  } else {
    _block = player.position;
  }
  let position = _block;
  return new Player(
    position,
    new $vector.Vector(
      player.velocity.x,
      player.velocity.y,
      player.velocity.z - player_gravity_strength,
    ),
    player.acceleration,
    player.last_fire_time,
    player.last_hit_time,
    player.current_health,
    player.max_health,
  );
}

/**
 * Renders the player to the screen.
 */
export function draw(p, player) {
  let $ = is_player_dead(player);
  let $1 = is_player_invulnerable(player);
  if ($) {
    $p5.fill(p, dead_fill_color)
  } else if ($1) {
    $p5.fill(p, invulnerable_fill_color)
  } else {
    $p5.fill(p, player_fill_color)
  }
  $p5.stroke(p, player_stroke_color);
  let $2 = $float.power(player_size, 1.0 + (player.position.z / 4.0));
  let size_to_draw;
  if ($2 instanceof Ok) {
    size_to_draw = $2[0];
  } else {
    throw makeError(
      "let_assert",
      FILEPATH,
      "player",
      209,
      "draw",
      "Pattern match failed, no pattern matched the value.",
      {
        value: $2,
        start: 5171,
        end: 5262,
        pattern_start: 5182,
        pattern_end: 5198
      }
    )
  }
  let size_to_draw$1 = $float.max(size_to_draw, player_size / 2.0);
  return $p5.ellipse(
    p,
    player.position.x,
    player.position.y,
    size_to_draw$1,
    size_to_draw$1,
  );
}
